import { INVOICE_STATUS, PAYMENT_METHODS } from './constants';

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0, // remove decimals if you want ₹2,00,000 instead of ₹2,00,000.00
  }).format(amount);
};

// Short currency formatter for charts (e.g., 1.2k, 1.2M)
export const formatShortCurrency = (amount: number): string => {
  const abs = Math.abs(amount);
  if (abs >= 1_00_00_000) {
    return `₹${(amount / 1_00_00_000).toFixed(1)}Cr`;
  } else if (abs >= 1_00_000) {
    return `₹${(amount / 1_00_000).toFixed(1)}L`;
  } else if (abs >= 1_000) {
    return `₹${(amount / 1_000).toFixed(1)}k`;
  }
  return `₹${amount}`;
};

export const formatDate = (date: string | Date): string => {
  return new Date(date).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (date: string | Date): string => {
  return new Date(date).toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case INVOICE_STATUS.PAID:
      return '#10B981';
    case INVOICE_STATUS.PARTIALLY_PAID:
      return '#F59E0B';
    case INVOICE_STATUS.OVERDUE:
      return '#EF4444';
    default:
      return '#6B7280';
  }
};

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case INVOICE_STATUS.PAID:
      return 'Paid';
    case INVOICE_STATUS.PARTIALLY_PAID:
      return 'Partially Paid';
    case INVOICE_STATUS.OVERDUE:
      return 'Overdue';
    default:
      return 'Pending';
  }
};

export const getPaymentMethodLabel = (method: string): string => {
  switch (method) {
    case PAYMENT_METHODS.CASH:
      return 'Cash';
    case PAYMENT_METHODS.BANK_TRANSFER:
      return 'Bank Transfer';
    case PAYMENT_METHODS.CARD:
      return 'Card';
    case PAYMENT_METHODS.CHECK:
      return 'Check';
    default:
      return 'Other';
  }
};

export const generateInvoiceNumber = (): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `INV-${timestamp}-${random}`;
};

export const calculateInvoiceTotal = (items: any[], tax: number = 0): number => {
  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  return subtotal + (subtotal * tax / 100);
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^\+?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const isToday = (date: string | Date): boolean => {
  const today = new Date();
  const checkDate = new Date(date);
  return (
    today.getFullYear() === checkDate.getFullYear() &&
    today.getMonth() === checkDate.getMonth() &&
    today.getDate() === checkDate.getDate()
  );
};

export const canEditInvoice = (invoiceDate: string, userRole: string): boolean => {
  if (userRole === 'admin') return true;
  return isToday(invoiceDate);
};

export function formatDateDMY(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export function formatDateTimeDMY(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

// Helper function to draw styled company name in PDFs
export function drawStyledCompanyName(pdf: any, companyName: string, yPos: number, pageWidth: number) {
  if (companyName.length > 0) {
    const firstLetter = companyName.charAt(0);
    const restOfName = companyName.slice(1);
    
    try {
      // Try to use brush script font if available
      pdf.addFont('brush-script.ttf', 'BrushScript', 'normal');
      
      // Measure first letter with brush script font
      pdf.setFont('BrushScript', 'normal');
      pdf.setFontSize(40);
      const firstLetterWidth = pdf.getTextWidth(firstLetter);
      
      // Measure rest with helvetica
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      const restWidth = pdf.getTextWidth(restOfName);
      
      const totalWidth = firstLetterWidth + restWidth;
      const startX = (pageWidth - totalWidth) / 2;
      
      // Draw first letter with brush script font
      pdf.setFont('BrushScript', 'normal');
      pdf.setFontSize(40);
      pdf.setTextColor(255, 0, 0); // Pure red color
      pdf.text(firstLetter, startX, yPos);
      
      // Draw rest of name (green, bold)
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.setTextColor(34, 197, 94); // Green color
      pdf.text(restOfName, startX + firstLetterWidth, yPos);
      
    } catch (error) {
      console.warn('Brush script font not available, falling back to helvetica');
      // Fallback to helvetica if brush script is not available
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(40);
      const firstLetterWidth = pdf.getTextWidth(firstLetter);
      
      pdf.setFontSize(18);
      const restWidth = pdf.getTextWidth(restOfName);
      
      const totalWidth = firstLetterWidth + restWidth;
      const startX = (pageWidth - totalWidth) / 2;
      
      pdf.setFontSize(40);
      pdf.setTextColor(255, 0, 0);
      pdf.text(firstLetter, startX, yPos);
      
      pdf.setFontSize(18);
      pdf.setTextColor(34, 197, 94);
      pdf.text(restOfName, startX + firstLetterWidth, yPos);
    }
  }
  
  // Reset text properties for subsequent content
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
} 
